// src/components/PresentationLinksManager.jsx
// Le collectionneur suit ses liens de présentation : statut, nombre de consultations,
// journal détaillé (date, adresse IP, navigateur), copie du lien et RÉVOCATION.
// RLS : le propriétaire ne voit que SES liens et le journal de ses liens.
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const fmtTime = (d) =>
  d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

function statusOf(l) {
  if (l.revoked_at) return { key: "revoked", label: "Révoqué", cls: "bg-gray-100 text-gray-600" };
  if (new Date(l.expires_at) <= new Date()) return { key: "expired", label: "Expiré", cls: "bg-amber-100 text-amber-700" };
  return { key: "active", label: "Actif", cls: "bg-emerald-100 text-emerald-700" };
}

// Navigateur lisible à partir du user-agent (sans bibliothèque).
function browserOf(ua) {
  if (!ua) return "—";
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Windows/.test(ua) ? "Windows" : /Mac OS X/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  const br = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Navigateur";
  return os ? `${br} · ${os}` : br;
}

export default function PresentationLinksManager() {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [names, setNames] = useState({});   // collection_id -> titre
  const [logs, setLogs] = useState({});     // link_id -> [log]
  const [open, setOpen] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    const { data: rows } = await supabase
      .from("presentation_links").select("*").eq("created_by", user.id).order("created_at", { ascending: false });
    const list = rows || [];
    setLinks(list);

    const colIds = [...new Set(list.map((l) => l.collection_id))];
    if (colIds.length) {
      const { data: cols } = await supabase.from("collections").select("id, title").in("id", colIds);
      setNames(Object.fromEntries((cols || []).map((c) => [c.id, c.title])));
    }

    const ids = list.map((l) => l.id);
    if (ids.length) {
      const { data: rowsLogs } = await supabase
        .from("presentation_view_logs")
        .select("presentation_link_id, viewed_at, ip_address, user_agent")
        .in("presentation_link_id", ids)
        .order("viewed_at", { ascending: false });
      const byLink = {};
      for (const g of rowsLogs || []) (byLink[g.presentation_link_id] ||= []).push(g);
      setLogs(byLink);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (l) => {
    if (!window.confirm("Révoquer ce lien ? Il cessera immédiatement de fonctionner.")) return;
    setBusy(l.id);
    await supabase.rpc("revoke_presentation_link", { p_share_token: l.share_token });
    setBusy("");
    load();
  };

  const copy = (l) => navigator.clipboard.writeText(`${window.location.origin}/presentation/shared/${l.session_token}`);

  if (loading || links.length === 0) return null; // n'apparaît que si l'utilisateur a des liens

  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">Accès contrôlé</p>
        <h2 className="mt-1 font-serif text-xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
          Mes liens de présentation
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Liens temporaires pour montrer une collection sans compte — chaque ouverture est journalisée, révocable à tout moment.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2 pr-3">Collection</th>
              <th className="py-2 px-3">Expire</th>
              <th className="py-2 px-3 text-right">Consultations</th>
              <th className="py-2 px-3">Statut</th>
              <th className="py-2 pl-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => {
              const st = statusOf(l);
              const entries = logs[l.id] || [];
              const n = Math.max(l.view_count || 0, entries.length);
              const last = l.last_viewed_at || entries[0]?.viewed_at;
              return (
                <React.Fragment key={l.id}>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 pr-3 font-medium text-gray-900">{names[l.collection_id] || "Collection"}</td>
                    <td className="py-2.5 px-3 text-gray-600">{fmt(l.expires_at)}</td>
                    <td className="py-2.5 px-3 text-right text-gray-700">
                      {n}{last ? <span className="text-gray-400"> · {fmt(last)}</span> : ""}
                      {entries.length > 0 && (
                        <button
                          onClick={() => setOpen(open === l.id ? "" : l.id)}
                          className="ml-2 text-xs text-gray-600 underline hover:text-black"
                        >
                          {open === l.id ? "Masquer" : "Détail"}
                        </button>
                      )}
                    </td>
                    <td className="py-2.5 px-3"><span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                    <td className="py-2.5 pl-3 text-right">
                      {st.key === "active" ? (
                        <div className="inline-flex gap-2">
                          <button onClick={() => copy(l)} className="text-xs text-gray-600 underline hover:text-black">Copier</button>
                          <button onClick={() => revoke(l)} disabled={busy === l.id} className="text-xs font-medium text-rose-700 underline hover:text-rose-900 disabled:opacity-50">
                            {busy === l.id ? "…" : "Révoquer"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                  {open === l.id && (
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <td colSpan={5} className="px-3 py-3">
                        <table className="w-full text-xs text-gray-600">
                          <thead>
                            <tr className="text-left uppercase tracking-wide text-gray-400">
                              <th className="py-1 pr-3">Date et heure</th>
                              <th className="py-1 px-3">Adresse IP</th>
                              <th className="py-1 pl-3">Navigateur</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.slice(0, 50).map((e, i) => (
                              <tr key={i}>
                                <td className="py-1 pr-3">{fmtTime(e.viewed_at)}</td>
                                <td className="py-1 px-3">{e.ip_address || "—"}</td>
                                <td className="py-1 pl-3">{browserOf(e.user_agent)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {entries.length > 50 && <p className="mt-2 text-xs text-gray-400">50 dernières consultations affichées.</p>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
